import {
  Component, Input, OnInit, Output, SimpleChanges, EventEmitter, ElementRef, ViewChild,
  OnChanges, OnDestroy,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  faExclamationCircle,
  faUnlockAlt,
  faEnvelope
} from '@fortawesome/pro-light-svg-icons';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';

@Component({
  selector: 'app-input-field',
  template: `
    <ng-container [formGroup]="parent">
      <fa-icon *ngIf="leadingIcon"
               class="leading-icon"
               [icon]="icons[leadingIcon]"
      ></fa-icon>
      <input
        #input
        [type]="type"
        [formControlName]="controlName"
        (change)="handleChange($event)"
        [textMask]="{mask: mask}"
        [ngStyle]="inputStyles"
        [ngClass]="classes"
        animateInput
        [error]="error"
        [inputValue]="parent.get(controlName).value"
        [label]="labelRef"
        [leadingIcon]="leadingIcon"
        [attr.data-encrypted-name]="dataEncryptedName"
        [labelToTop]="autofillState.start || (checkAutofill && !autofillState.cancel)"
      >
      <label
        #labelRef
        [style.backgroundColor]="parent.get(controlName).disabled ? 'transparent' : '#ffffff'"
        [ngClass]="classes"
        (click)="input.focus()">{{label}}</label>
      <ng-container *ngIf="error">
        <fa-icon
          class="error-icon trailing-icon"
          [icon]="icons.faExclamationCircle"
          [ngStyle]="{color: '#FF4F58'}"></fa-icon>
      </ng-container>
      <validation-error
        [showError]="error"
        [errors]="error && parent.get(this.controlName)?.errors"
        [name]="errorLabel || label">
      </validation-error>
    </ng-container>
  `,
  styleUrls: ['../common.scss'],
})

export class InputFieldComponent implements OnInit, OnChanges, OnDestroy {
  error = false;
  previousStatus: string;
  icons = {
    faUnlockAlt: faUnlockAlt,
    faExclamationCircle: faExclamationCircle,
    faEnvelope: faEnvelope
  };
  onAnimationStart: (event: AnimationEvent) => void;
  autofillState: { start: boolean; cancel: boolean } = {start: false, cancel: false};
  private unsubscribe: Subject<void> = new Subject();

  @Input() type: string;
  @Input() label: string;
  @Input() errorLabel: string;
  @Input() parent: FormGroup;
  @Input() controlName: string;
  @Input() submitted: boolean;
  @Input() maskName: string;
  @Input() leadingIcon: string;
  @Input() trailingIcon: string;
  @Input() isReseting: boolean;
  @Input() matDatepicker: any;
  @Input() dataEncryptedName: string;
  @Input() onlyNumber: boolean;
  @Input() disabled: boolean;
  @Input() checkAutofill: boolean = false;

  @Output() change: EventEmitter<HTMLInputElement> = new EventEmitter();
  @Output() setIsReseting: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('input') input: ElementRef;

  ngOnInit() {
    this.parent.get(this.controlName) && this.parent.get(this.controlName).statusChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(status => {
        this.error = status === 'INVALID' && this.previousStatus !== 'DISABLED';
        this.previousStatus = status;
      });

    this.onAnimationStart = ({target, animationName}) => {
      if (animationName === 'onAutoFillStart') {
        this.autofillState = {...this.autofillState, start: true};
        this.input.nativeElement.removeEventListener('animationstart', this.onAnimationStart);
      } else if (animationName === 'onAutoFillCancel') {
        this.autofillState = {...this.autofillState, cancel: true};
      }
    };
    this.input.nativeElement.addEventListener('animationstart', this.onAnimationStart);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.submitted &&
      changes.submitted.currentValue &&
      this.parent.get(this.controlName) &&
      this.parent.get(this.controlName).invalid
    ) {
      this.error = true;
    }
    if (this.parent.get(this.controlName).valid) {
      this.error = false;
    }
    if (changes.isReseting && changes.isReseting.currentValue) {
      this.error = false;
    }
  }

  handleChange(event) {
    this.change.emit(event);
  }

  get mask() {
    switch (this.maskName) {
      case 'cardNumMask':
        return [/\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/];
      case 'expDateMask':
        return [/\d/, /\d/, '/', /\d/, /\d/];
      case 'cvcMask':
        return [/\d/, /\d/, /\d/];
      case 'regNumberMask':
        return [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
      case 'dateMask':
        return [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
      default:
        return false;
    }
  }

  get inputStyles() {
    return this.leadingIcon ? {paddingLeft: '34px'} : {};
  }

  get classes() {
    let classes = '';
    if (this.leadingIcon) {
      classes += 'leading-icon ';
    }
    if (this.disabled) {
      classes += 'disabled ';
    }
    return classes;
  }

  ngOnDestroy() {
    this.input.nativeElement.removeEventListener('animationstart', this.onAnimationStart);
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/form-fields/input-field/input-field.component.ts